import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../Context/UserContext";
import useAPI from "../../Hooks/useAPI";
import Header2 from "../../Components/Header2/Header2";
import { default as Footer } from "../../Components/Footer/FooterSmall";
import {
  faArrowLeft,
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "../../Components/Page/Page.css";
import "./UserNotifications.css";

interface User {
  id: string;
  email: string;
  firstname: string;
  lastname: string;
  role: string;
  createdat: string;
  mndaStatus: string;
}

interface UserNotifications {
  messageid: number;
  userid: string;
  messagetext: string;
  startdate: string;
  enddate: string;
  sentby: string;
  dateadded: string;
  firstname: string;
  lastname: string;
  recipients: string;
  email: string;
  smessagetypedescr: string;
  dateread: string;
}

interface NotificationType {
  imessagetypeid: number;
  messagetype: string;
  messagetypedescr: string;
}

export default function UserNotifications() {
  const [users, setUsers] = useState<User[]>([]);
  const [usernotifications, setUserNotifications] = useState<
    UserNotifications[]
  >([]);
  const [notificationtypes, setNotificationTypes] = useState<
    NotificationType[]
  >([]);

  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { user, hasRole } = useUser();
  const navigate = useNavigate();
  const { getUsers, getNotificationTypes, getUserNotifications } = useAPI();
  const [selectedUserId, setSelectedUserId] = useState("all");
  const [selectedViewBy, setSelectedViewBy] = useState("message");
  const [selectedNotificationType, setSelectedNotificationType] =
    useState("all");

  const [sortConfig, setSortConfig] = useState<{
    key: keyof UserNotifications | null;
    direction: "ascending" | "descending" | null;
  }>({
    key: null,
    direction: null,
  });

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");
      const response = await getUsers("");
      if (response?.data) {
        setUsers(response.data);
      } else {
        throw new Error("Failed to fetch users");
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred during fetchUsers",
      );
    } finally {
      setIsLoading(false);
    }
  }, [getUsers]);

  const fetchNotificationTypes = useCallback(async () => {
    try {
      const response = await getNotificationTypes();
      if (response?.data) {
        setNotificationTypes(response.data);
      } else {
        throw new Error("Failed to fetch Notification Types");
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred during fetchNotificationTypes",
      );
    }
  }, []);

  const fetchUserNotifications = useCallback(
    async (
      selViewBy: string,
      selUserid: string,
      selNotificationType: string,
    ) => {
      try {
        setIsLoading(true);
        setError("");
        //const selStatus = selectedContractStatus || "all";
        const response = await getUserNotifications(
          selViewBy,
          selUserid,
          selNotificationType,
        );
        if (response?.data) {
          setUserNotifications(response.data);
        } else {
          throw new Error("Failed to fetch user notifications");
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "An error occurred during fetchUserNotifications",
        );
      } finally {
        setIsLoading(false);
      }
    },
    [getUserNotifications],
  );

  useEffect(() => {
    // Check if user is logged in
    if (!user) {
      navigate("/login");
      return;
    }

    // Check if user has administrator role
    if (!hasRole("administrator")) {
      navigate("/");
      return;
    }

    fetchUsers();
    fetchNotificationTypes();
  }, []);

  useEffect(() => {
    fetchUserNotifications(
      selectedViewBy,
      selectedUserId,
      selectedNotificationType,
    );
  }, [selectedViewBy, selectedUserId, selectedNotificationType]);

  const handleSort = (key: keyof UserNotifications) => {
    let direction: "ascending" | "descending" | null = "ascending";

    if (sortConfig.key === key) {
      if (sortConfig.direction === "ascending") {
        direction = "descending";
      } else if (sortConfig.direction === "descending") {
        direction = null;
      }
    }

    setSortConfig({ key, direction });
  };

  const getSortedUsers = () => {
    let filteredUsers = usernotifications;

    if (!sortConfig.key || !sortConfig.direction) {
      return filteredUsers;
    }

    return [...filteredUsers].sort((a, b) => {
      const aValue = a[sortConfig.key!];
      const bValue = b[sortConfig.key!];

      // Handle different data types
      let aStr: string;
      let bStr: string;

      if (Array.isArray(aValue)) {
        aStr = aValue.join(", ");
        bStr = Array.isArray(bValue) ? bValue.join(", ") : "";
      } else if (typeof aValue === "boolean") {
        aStr = aValue ? "Yes" : "No";
        bStr = typeof bValue === "boolean" ? (bValue ? "Yes" : "No") : "";
      } else if (typeof aValue === "number") {
        aStr = aValue.toString();
        bStr = typeof bValue === "number" ? bValue.toString() : "";
      } else {
        aStr = aValue ? aValue.toString().toLowerCase() : "";
        bStr = bValue ? bValue.toString().toLowerCase() : "";
      }

      if (aStr < bStr) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (aStr > bStr) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortIcon = (columnKey: keyof UserNotifications) => {
    if (sortConfig.key !== columnKey) {
      return <FontAwesomeIcon icon={faSort} />;
    }
    return sortConfig.direction === "ascending" ? (
      <FontAwesomeIcon icon={faSortUp} />
    ) : (
      <FontAwesomeIcon icon={faSortDown} />
    );
  };

  const formatRecipients = (
    recipients: string | null | undefined,
    maxVisible: number = 10,
  ): string => {
    if (!recipients) return "";

    const list = recipients
      .split(";")
      .map((s) => s.trim())
      .filter(Boolean);

    if (list.length <= maxVisible) return list.join("; ");

    const visible = list.slice(0, maxVisible);
    const remaining = list.length - maxVisible;

    return `${visible.join("; ")}; +${remaining} more`;
  };

  const handleEdit = (messageid: number) => {
    navigate(`/UserNotifications/edit/${messageid}`);
  };

  const handleAdd = () => {
    navigate("/UserNotifications/add");
  };

  return (
    <div className="generalPage">
      <Header2 />
      <div className="usermanagement-container">
        <div className="pageContainer">
          <div className="button-container">
            <button className="back-button" onClick={() => navigate("/")}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Home
            </button>
          </div>

          <div className="usermanagement-header">
            <div className="usermanagement-actions">
              <h1 className="usermanagement-title">User Notifications</h1>
            </div>
          </div>
          <div className="usernotifications-actions">
            <button onClick={handleAdd} className="add-user-button">
              Add New Notification
            </button>
          </div>
          <div className="flex items-center gap-4 p-4">
            <label className="marginleft">View By </label>
            <select
              onChange={(e) => setSelectedViewBy(e.target.value)}
              value={selectedViewBy ?? "message"}
              className="usernotifications-select"
            >
              <option value="message">Message</option>
              <option value="users">Users</option>
            </select>

            {selectedViewBy === "users" && (
              <>
                <label className="marginright">User</label>

                <select
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  value={selectedUserId ?? ""}
                >
                  <option value="all">-- All Users --</option>
                  {users.map((u, uindex) => (
                    <option key={uindex} value={u.id}>
                      {u.firstname} {u.lastname} - {u.email}
                    </option>
                  ))}
                </select>
              </>
            )}
            <label className="marginright marginleft">Notification Type</label>
            <select
              onChange={(e) => setSelectedNotificationType(e.target.value)}
              value={selectedNotificationType}
            >
              <option value="all">-- All Notification Types --</option>
              {notificationtypes.map((at, atindex) => (
                <option key={atindex} value={at.messagetype}>
                  {at.messagetypedescr}
                </option>
              ))}
            </select>
          </div>
          {error && <div className="error-message">{error}</div>}

          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <div className="loading-text">Loading...</div>
            </div>
          ) : (
            <div className="usernotification-table-container">
              <table className="usernotification-table">
                <colgroup>
                  {selectedViewBy === "users" ? (
                    <>
                      <col style={{ width: "15%" }} />
                      {/* message text */}
                      <col style={{ width: "7%" }} />
                      {/* notification type */}
                      <col style={{ width: "8%" }} />
                      {/* sent by */}
                      <col style={{ width: "6%" }} />
                      {/* date sent */}
                      <col style={{ width: "7%" }} />
                      {/* start date */}
                      <col style={{ width: "6%" }} />
                      {/* end date */}
                      <col style={{ width: "10%" }} /> {/* recipients */}
                      <col style={{ width: "12%" }} />
                      {/* email */}
                      <col style={{ width: "6%" }} />
                      {/* date read */}
                      <col style={{ width: "4%" }} />
                      {/* action */}
                    </>
                  ) : (
                    <>
                      <col style={{ width: "20%" }} />
                      {/* message text */}
                      <col style={{ width: "8%" }} />
                      {/* notification type */}
                      <col style={{ width: "7%" }} />
                      {/* sent by */}
                      <col style={{ width: "5%" }} />
                      {/* date sent */}
                      <col style={{ width: "6%" }} />
                      {/* start date */}
                      <col style={{ width: "5%" }} />
                      {/* end date */}
                      <col style={{ width: "12%" }} /> {/* recipients */}
                      <col style={{ width: "3%" }} />
                      {/* action */}
                    </>
                  )}
                </colgroup>
                <thead className="usernotification-table-header">
                  <tr>
                    <th
                      onClick={() => handleSort("messagetext")}
                      className="sortable-header"
                    >
                      Message {getSortIcon("messagetext")}
                    </th>
                    <th
                      onClick={() => handleSort("smessagetypedescr")}
                      className="sortable-header"
                    >
                      Notification Type {getSortIcon("smessagetypedescr")}
                    </th>
                    <th
                      onClick={() => handleSort("sentby")}
                      className="sortable-header"
                    >
                      Sent By {getSortIcon("sentby")}
                    </th>
                    <th
                      onClick={() => handleSort("dateadded")}
                      className="sortable-header"
                    >
                      Date Sent {getSortIcon("dateadded")}
                    </th>
                    <th
                      onClick={() => handleSort("startdate")}
                      className="sortable-header"
                    >
                      Start Date {getSortIcon("startdate")}
                    </th>
                    <th
                      onClick={() => handleSort("enddate")}
                      className="sortable-header"
                    >
                      End Date {getSortIcon("enddate")}
                    </th>
                    <th
                      onClick={() => handleSort("lastname")}
                      className="sortable-header"
                    >
                      Recipient {getSortIcon("lastname")}
                    </th>
                    {selectedViewBy === "users" && (
                      <>
                        <th
                          onClick={() => handleSort("email")}
                          className="sortable-header"
                        >
                          Recipient Email {getSortIcon("email")}
                        </th>
                        <th
                          onClick={() => handleSort("dateread")}
                          className="sortable-header"
                        >
                          Date Read {getSortIcon("dateread")}
                        </th>
                      </>
                    )}
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedUsers().map((usernotifications) => (
                    <tr
                      key={`${usernotifications.messageid}-${usernotifications.userid}`}
                      className="usernotification-table-row"
                    >
                      <td>{usernotifications.messagetext}</td>
                      <td>{usernotifications.smessagetypedescr}</td>
                      <td>{usernotifications.sentby}</td>
                      <td>
                        {usernotifications.dateadded
                          ? new Date(usernotifications.dateadded)
                              .toISOString()
                              .split("T")[0]
                          : ""}
                      </td>
                      <td>
                        {usernotifications.startdate
                          ? new Date(usernotifications.startdate)
                              .toISOString()
                              .split("T")[0]
                          : ""}
                      </td>
                      <td>
                        {usernotifications.enddate
                          ? new Date(usernotifications.enddate)
                              .toISOString()
                              .split("T")[0]
                          : ""}
                      </td>
                      <td title={usernotifications.recipients}>
                        {formatRecipients(usernotifications.recipients)}
                      </td>
                      {selectedViewBy === "users" && (
                        <>
                          <td>{usernotifications.email}</td>
                          <td>
                            {usernotifications.dateread
                              ? new Date(usernotifications.dateread)
                                  .toISOString()
                                  .split("T")[0]
                              : ""}
                          </td>
                        </>
                      )}
                      <td>
                        <button
                          onClick={() =>
                            handleEdit(usernotifications.messageid)
                          }
                          className="action-button edit-button"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
